// ContactActions — row of round icon links for a contact's identities
// (phone/email/messenger deep links). Every href is scheme-checked against an
// allowlist before it is ever rendered as a clickable anchor — an unsafe or
// null href renders a non-interactive placeholder instead. i18n-clean:
// `label` arrives via props; imports only from `./` (ui), never a feature.
import type { CSSProperties } from 'react';
import { useState } from 'react';
import { Icon, type IconName } from './Icon.js';

export interface ContactAction {
  icon: IconName;
  href: string | null;
  label: string;
}

export interface ContactActionsProps {
  actions: ContactAction[];
}

const MAX_ACTIONS = 5;

const ITEM_BASE: CSSProperties = {
  width: 24,
  height: 24,
  borderRadius: 'var(--r-full)',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  flex: 'none',
};

// DANGER: allowlist, not a blocklist — `javascript:`/`data:`/relative/malformed
// hrefs must all fall through to the disabled placeholder, never an anchor.
const HTTP_PREFIXES = ['https://', 'http://'];
const DIRECT_PREFIXES = ['tel:', 'mailto:'];

function safeScheme(href: string): 'http' | 'direct' | null {
  const normalized = href.trim().toLowerCase();
  if (DIRECT_PREFIXES.some((p) => normalized.startsWith(p))) return 'direct';
  if (HTTP_PREFIXES.some((p) => normalized.startsWith(p))) {
    // Parse-and-validate: a bare `http://` (no host) or whitespace-mangled
    // href must fall through to the placeholder, never render as an anchor.
    try {
      const u = new URL(href.trim());
      if ((u.protocol === 'http:' || u.protocol === 'https:') && u.hostname) return 'http';
    } catch {
      // malformed → placeholder
    }
  }
  return null;
}

function ContactActionItem({ icon, href, label }: ContactAction) {
  const [hover, setHover] = useState(false);
  const scheme = href === null ? null : safeScheme(href);

  if (scheme === null) {
    // Non-interactive by construction: a <span> has no default focus/click
    // behavior, so this can never become a clickable link.
    return (
      <span
        aria-disabled="true"
        title={label}
        aria-label={label}
        style={{ ...ITEM_BASE, color: 'var(--fg-4)' }}
      >
        <Icon name={icon} size={14} />
      </span>
    );
  }

  return (
    <a
      href={href as string}
      title={label}
      aria-label={label}
      target={scheme === 'http' ? '_blank' : undefined}
      rel={scheme === 'http' ? 'noreferrer' : undefined}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        ...ITEM_BASE,
        color: hover ? 'var(--accent-1-press)' : 'var(--fg-2)',
        background: hover ? 'var(--accent-1-subtle)' : 'transparent',
        textDecoration: 'none',
      }}
    >
      <Icon name={icon} size={14} />
    </a>
  );
}

export function ContactActions({ actions }: ContactActionsProps) {
  if (actions.length === 0) return null;

  return (
    <span style={{ display: 'inline-flex', gap: 2 }}>
      {actions.slice(0, MAX_ACTIONS).map((action, i) => (
        <ContactActionItem key={i} {...action} />
      ))}
    </span>
  );
}
